import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { Subject } from 'rxjs';
import { CarerReliabilityEvaluation } from 'src/app/shared/models/gql/carer-reliability-evaluation.model';
import { ReliabilityFeedback } from 'src/app/shared/models/gql/carer-reliability.model';
import { ApiInterfaceService } from 'src/app/shared/services/api-interface.service';

@Injectable({
  providedIn: 'root',
})
export class ReliabilityAssessmentService {
  // Observable to handle events to refresh data
  private refreshData: Subject<void> = new Subject();

  /*
  Contain the assessment data such as worker name, period date of assessment and assessment type and
  will cleanup the data when the modal is destroyed.
  */
  assessmentData: CarerReliabilityEvaluation;

  constructor(private api: ApiInterfaceService) {}

  /**
   * Return observable to listen to it for refreshing data.
   */
  onRefreshData() {
    return this.refreshData.asObservable();
  }

  /**
   * Fire event to refresh data from modal.
   */
  refresh() {
    this.refreshData.next();
  }

  /**
   * Send request to submit assessment.
   *
   * @param feedback - Assessment form data
   * @param optOutOfFutureNotifications - boolean that is true when the user wishes to opt out of future carer reliability assessments, false or undefined otherwise.
   */
  async postCarerReliabilityFeedback(feedback: ReliabilityFeedback, optOutOfFutureNotifications?: boolean) {
    try {
      // string used for opting out in GraphQL mutation if optOutOfFutureNotifications is true
      const optOutString = optOutOfFutureNotifications
        ? `
        userAccountUpdate(input: {
          userAttributes: {
            optInCarerReliabilityAssessment: false
          }
        }) {
          user {
            id
            optInCarerReliabilityAssessment
          }
        }
      `
        : '';

      const res = await this.api.gqlMutation(
        gql`
      mutation ($feedbackInput:UpdateCarerReliabilityAssessmentInput!) {
        updateCarerReliabilityAssessment(input:$feedbackInput){
          clientMutationId
        }
        ${optOutString}
      }`,
        { feedbackInput: feedback },
      );
      return res['updateCarerReliabilityAssessment'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Send mutation to opt out of reliability feedback.
   */
  async optOutReliabilityFeedback() {
    try {
      return await this.api.gqlMutation(gql`
        mutation {
          userAccountUpdate(input: { userAttributes: { optInCarerReliabilityAssessment: false } }) {
            user {
              id
              optInCarerReliabilityAssessment
            }
          }
        }
      `);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get reliability assessment data
   *
   * @param id - assessment id
   */
  async getReliabilityAssessmentData(id) {
    try {
      const res = await this.api.gqlQuery(gql`
      query{
        carerReliabilityAssessment(id:${id}){
          periodStartDate
          periodEndDate
          isFirstWeekly
          agreement{
            otherUser{
              firstName
            }
          }
        }
      }`);
      return res['carerReliabilityAssessment'];
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
